import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { usePortal } from '@/lib/portal';
import { useSession } from '@/lib/session';

/**
 * The front door of a tenant's catalog portal.
 *
 * The hostname already names the organisation, so there is no realm to
 * discover: its identity providers are offered directly, and a password form
 * only appears if it is genuinely an option.
 */
export function PortalSignInPage() {
  const { context, organisation } = usePortal();
  const { login } = useSession();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(params.get('sso_error'));
  const [showPassword, setShowPassword] = useState(false);

  const branding = context?.portal?.branding;
  const sso = context?.auth?.sso ?? [];
  const passwordLogin = context?.auth?.passwordLogin ?? true;
  const accent = branding?.accentColor ?? '#2f34c9';

  const startSso = (connectionId: string) => {
    const next = params.get('next');
    const query = new URLSearchParams({ connectionId });
    if (next?.startsWith('/')) query.set('redirectTo', next);
    window.location.assign(`/api/auth/sso/start?${query.toString()}`);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({ email: form.email, password: form.password });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign you in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-full place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          {branding?.logoDataUri ? (
            <img src={branding.logoDataUri} alt={organisation ?? ''} className="mx-auto mb-4 max-h-14 max-w-56 object-contain" />
          ) : (
            <div
              className="mx-auto mb-4 grid size-12 place-items-center rounded-xl text-lg font-bold text-white"
              style={{ background: accent }}
            >
              {(organisation ?? '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <h1 className="text-xl font-semibold text-ink-900">{branding?.title || `${organisation} policy library`}</h1>
          <p className="mt-1.5 text-[13.5px] text-ink-500">
            {branding?.tagline || 'Sign in to read the policies that apply to you.'}
          </p>
        </div>

        <Card className="p-5">
          {error ? (
            <div className="mb-4">
              <Alert tone="danger">{error}</Alert>
            </div>
          ) : null}

          {sso.length ? (
            <div className="space-y-2">
              {sso.map((connection) => (
                <Button
                  key={connection.id}
                  variant="primary"
                  size="lg"
                  className="w-full"
                  icon={<KeyRound className="size-4" />}
                  style={{ background: accent }}
                  onClick={() => startSso(connection.id)}
                >
                  Continue with {connection.name}
                </Button>
              ))}
            </div>
          ) : null}

          {sso.length && passwordLogin ? (
            <div className="my-4 flex items-center gap-3">
              <span className="h-px flex-1 bg-ink-200" />
              <span className="text-[11.5px] text-ink-400">or</span>
              <span className="h-px flex-1 bg-ink-200" />
            </div>
          ) : null}

          {passwordLogin && (!sso.length || showPassword) ? (
            <form onSubmit={submit} className="space-y-4">
              <Field label="Email address" required>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  autoComplete="email"
                  autoFocus={!sso.length}
                />
              </Field>
              <Field label="Password" required>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="current-password"
                />
              </Field>
              <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full" style={{ background: accent }}>
                Sign in
              </Button>
            </form>
          ) : passwordLogin && sso.length ? (
            <button
              type="button"
              onClick={() => setShowPassword(true)}
              className="w-full text-center text-[13px] text-ink-500 hover:text-ink-800"
            >
              Sign in with a password instead
            </button>
          ) : null}

          {!passwordLogin && !sso.length ? (
            <Alert tone="warning" title="No way to sign in">
              This portal has no identity provider configured and password sign-in is switched off. Contact your administrator.
            </Alert>
          ) : null}
        </Card>

        <p className="mt-6 text-center text-[12px] text-ink-400">
          {branding?.supportEmail ? (
            <>
              Need help?{' '}
              <a href={`mailto:${branding.supportEmail}`} className="underline hover:text-ink-600">
                {branding.supportEmail}
              </a>
            </>
          ) : (
            `${organisation ?? 'This organisation'} · policy library`
          )}
        </p>
      </div>
    </div>
  );
}
